import { DOCUMENT, Location } from '@angular/common';
import { EnvironmentProviders, inject, InjectionToken, provideAppInitializer } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';

/** sessionStorage key set while this tab is reloading for a stale chunk, until a navigation succeeds. */
export const STALE_CHUNK_RELOAD_KEY = '__stale_chunk_reload';

/** The window to reload. A token so specs can swap it: jsdom can't navigate. */
export const STALE_CHUNK_WINDOW = new InjectionToken<Pick<Window, 'location' | 'sessionStorage'> | null>(
	'STALE_CHUNK_WINDOW',
	{ providedIn: 'root', factory: () => inject(DOCUMENT).defaultView },
);

// How Chrome and Edge, Firefox, and Safari word a lazy chunk that failed to load.
const CHUNK_LOAD_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

export function isChunkLoadError(error: unknown): boolean {
	return error instanceof Error && CHUNK_LOAD_ERROR.test(error.message);
}

// Windows that `reloadOnStaleChunk` is reloading. In memory, so the reloaded page starts without its window here.
const reloadingWindows = new WeakSet<object>();

/**
 * For Sentry's `beforeSend`: true for the chunk error of a page that `reloadOnStaleChunk` is reloading. That's a
 * tab left open across a deploy, not a bug. A chunk that is still missing after the reload is reported as usual.
 */
export function isStaleChunkReloadError(error: unknown, window: object | null = globalThis.window ?? null): boolean {
	return window !== null && reloadingWindows.has(window) && isChunkLoadError(error);
}

/**
 * Every deploy replaces the lazy-loaded chunks, and Cloudflare Pages answers the old chunk URLs with
 * index.html. A tab opened before the deploy then fails each navigation to a route it hasn't loaded yet,
 * and the link seems to do nothing. Load the target URL in full instead: that fetches the new build, and
 * the login survives because it lives in sessionStorage.
 *
 * For `withNavigationErrorHandler`, which runs it in an injection context. Use with
 * `provideStaleChunkReloadReset()`.
 */
export function reloadOnStaleChunk(navigationError: NavigationError): void {
	const window = inject(STALE_CHUNK_WINDOW);

	if (!window || !isChunkLoadError(navigationError.error)) {
		return;
	}

	// Reloaded already and no navigation has succeeded since: the chunk is really missing, so reloading
	// again would loop. Leave the error to Sentry.
	if (window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) !== null) {
		return;
	}

	window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, navigationError.url);
	reloadingWindows.add(window);
	window.location.assign(inject(Location).prepareExternalUrl(navigationError.url));
}

/** Lets `reloadOnStaleChunk` reload again once a navigation has succeeded. */
export function provideStaleChunkReloadReset(): EnvironmentProviders {
	return provideAppInitializer(() => {
		const window = inject(STALE_CHUNK_WINDOW);

		inject(Router).events
			.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe(() => window?.sessionStorage.removeItem(STALE_CHUNK_RELOAD_KEY));
	});
}
